import type { ApprovalMethod } from "@entrilia/shared/api/workflowTypes";
import type { ClientDictionaries } from "../../../../api/types/clientTypes";
import type {
  Field,
  ObjectFilter,
  ObjectTypeToLookupReferences,
  SelectOption,
} from "../../../../api/types/objectTypes";
import type { ApprovalRule } from "../../../../api/types/workflowTypes";
import { getSelectOptionsFromDictionary } from "../../../../utilities/dictionariesHelper";

export const getApprovalConfigurationMethodDescription = (method: ApprovalMethod): string => {
  if (method.requireAll) {
    return "All members of the listed groups must approve";
  }

  if (method.minimumApprovals === 1) {
    return "Any member of the listed groups can approve";
  }

  return `At least ${method.minimumApprovals} members of the listed groups must approve`;
};

export type ApprovalRuleObjectFilterDefaults = Omit<ObjectFilter, "fieldId">;

export const getApprovalRuleSelectOptions = (
  field: Field | undefined,
  lookupReferences: ObjectTypeToLookupReferences | undefined,
  dictionaries: ClientDictionaries
): SelectOption[] | undefined => {
  if (field?.configuration?.$type === "UserDefinedOptionsSelect") {
    return field.configuration.userDefinedOptions;
  }

  if (field?.configuration?.$type === "Select") {
    return getSelectOptionsFromDictionary(field.configuration.fieldType, dictionaries);
  }

  if (field?.configuration?.$type === "Lookup") {
    return (lookupReferences?.[field.configuration.objectType] ?? []).map((option) => ({
      value: option.objectId,
      label: [option.primaryFieldValue, option.secondaryFieldValue].filter(Boolean).join(" — "),
    }));
  }

  return undefined;
};

export const getApprovalRuleObjectFilter = (rule: ApprovalRule): ObjectFilter | undefined => {
  if (rule.numberFilter) {
    return { fieldId: rule.fieldId ?? "", numberFilter: rule.numberFilter };
  }

  if (rule.textFilter) {
    return { fieldId: rule.fieldId ?? "", textFilter: rule.textFilter };
  }

  if (rule.dateFilter) {
    return { fieldId: rule.fieldId ?? "", dateFilter: rule.dateFilter };
  }

  if (rule.booleanFilter) {
    return { fieldId: rule.fieldId ?? "", booleanFilter: rule.booleanFilter };
  }

  if (rule.multiSelectFilter) {
    return { fieldId: rule.fieldId ?? "", multiSelectFilter: rule.multiSelectFilter };
  }

  if (rule.guidFilter) {
    return { fieldId: rule.fieldId ?? "", guidFilter: rule.guidFilter };
  }

  return undefined;
};
